import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSendlaneCRUDActions } from '@mm/state/actions/SendlaneCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import ConnectionModel, {
  ConnectionKind,
  IntegrationTypes,
  IntegrationTypesToWebhookKind,
} from '@mm/state/model/ConnectionModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useSelector } from 'react-redux';
import { useConnectionWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { humanizedIntegrationNames } from '../../../../../Dashboard/ActivationBlock/use-activation-steps';
import { useConnectGoHighLevel } from '../../../../../Integrations/NativeIntegrations/components/GoHighLevelIntegrations/use-connect-gohighlevel';
import { useConnectKlaviyo } from '../../../../../Integrations/NativeIntegrations/components/KlaviyoIntegrations/use-connect-klaviyo';
import { StoreState } from '@mm/state/store-interfaces';
import { autoIncrementedName } from '../../../../../../../../../common/helpers/name-autoincrement';
import { selectConnectionWidgetsByType } from '@mm/state/modules/connectionsTableState/selectors';
import { useConnectShopify } from '../../../../../Integrations/NativeIntegrations/components/ShopifyIntegrations/use-connect-shopify';
import { useConnectSalesForce } from '../../../../../Integrations/NativeIntegrations/components/SalesForceIntegrations/use-connect-salesforce';

export const useIntegrationWidget = (
  type: IntegrationTypes,
  widget?: WidgetModel,
) => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const sendlaneLists = useSelector(
    (store: StoreState) => store.sendlane.lists,
  );
  const sendlaneApiKeys = useSelector(
    (store: StoreState) => store.sendlane.apiKeys,
  );
  const widgetConnections: ConnectionModel[] = useSelector(
    (state: StoreState) => selectConnectionWidgetsByType(state, type),
  );
  const salesForceTagsEnabled = useSelector(
    (state: StoreState) => state.salesForce.tagsEnabled,
  );

  const { retrieveWidgetConnections, create, update } =
    useConnectionCRUDActions();
  const { getLists: getSendlaneLists } = useSendlaneCRUDActions();
  const { accounts } = useConnectShopify();
  const shopifyAccounts = accounts.filter(token => token.status !== 'revoked');

  const { accounts: salesForceAccounts } = useConnectSalesForce();

  const {
    accounts: goHighLevelAccounts,
    getGoHighLevelLocations: getGoHighLevelLists,
    locations,
  } = useConnectGoHighLevel();

  const {
    getLists: getKlaviyoLists,
    accounts: klaviyoOAuthAccounts,
    apiKeys: klaviyoApiKeys,
    klaviyoLists,
  } = useConnectKlaviyo();

  const klaviyoAuthParams = {
    klaviyoParams: {
      accessTokenId:
        widgetConnections?.find(c => c.klaviyoAccessToken)?.klaviyoAccessToken
          ?.id || klaviyoApiKeys?.[0]?.id,
    },
  };

  const viewActions = useViewActions();
  const connectionWidgetHandlers = useConnectionWidgetHandlers();

  const humanizedIntegrationName = humanizedIntegrationNames[type || ''];

  const getGoHighLevelTokenId = (connection?: ConnectionModel) =>
    connection?.gohighlevelOauthToken?.id;
  const getKlaviyoTokenId = (connection?: ConnectionModel) =>
    connection?.klaviyoOauthToken?.id || connection?.klaviyoAccessToken?.id;
  const getSendlaneTokenId = (connection?: ConnectionModel) =>
    connection?.sendlaneAccessToken?.id;
  const getSalesForceTokenId = (connection?: ConnectionModel) =>
    connection?.salesforceAccessToken?.id;
  const getShopifyTokenId = (connection?: ConnectionModel) =>
    connection?.accessToken?.id;

  const getIntegrationWidgetConfig = (type: IntegrationTypes) => {
    switch (type) {
      case 'salesforce':
        return {
          accounts: salesForceAccounts,
          tagsDisabled: !salesForceTagsEnabled,
          getTokenId: getSalesForceTokenId,
          authParams: {
            salesforceParams: { accessTokenId: salesForceAccounts[0]?.id },
          },
        };

      case 'shopify':
        return {
          accounts: shopifyAccounts,
          getTokenId: getShopifyTokenId,
          authParams: {
            shopifyParams: {
              tokenId: shopifyAccounts[shopifyAccounts.length - 1]?.id,
              tokenType: shopifyAccounts[0]?.type,
            },
          },
        };
      case 'gohighlevel':
        return {
          accounts: goHighLevelAccounts,
          getLists: getGoHighLevelLists,
          lists: locations,
          getTokenId: getGoHighLevelTokenId,
          authParams: {
            gohighlevelParams: {
              oauthTokenId: goHighLevelAccounts[0]?.id,
            },
          },
        };
      case 'klaviyo':
        return {
          accounts: klaviyoOAuthAccounts.length
            ? klaviyoOAuthAccounts
            : klaviyoApiKeys,
          lists: klaviyoLists.map(list => ({
            id: list.id,
            name: list.attributes.name,
          })),
          getLists: getKlaviyoLists,
          getTokenId: getKlaviyoTokenId,
          authParams: klaviyoAuthParams,
        };
      case 'sendlane':
        return {
          accounts: sendlaneApiKeys,
          lists: sendlaneLists,
          getLists: getSendlaneLists,
          getTokenId: getSendlaneTokenId,
          authParams: {
            sendlaneParams: { accessTokenId: sendlaneApiKeys[0]?.id },
          },
        };
      default:
        return {};
    }
  };

  const fetchConnections = async () => {
    await retrieveWidgetConnections(activeFbPageId, type);
  };

  const onConnectionChange = async (connection: ConnectionModel) => {
    const { sendDataWidgetConfig: config } = connection;
    if (config && widget?.id) {
      const clonedWidget = {
        ...widget,
        config: {
          ...config,
          kind: type as any,
        },
      };
      await connectionWidgetHandlers.update(clonedWidget);
    }
  };

  const onEditConnection = (connectionWebhookId: number) => {
    viewActions.showSlideoutMenu('automationWidgetIntegrationEditor', {
      connectionId: connectionWebhookId,
      widget,
      type,
    });
  };

  const incrementedName = widgetConnections?.length
    ? autoIncrementedName(
        `${humanizedIntegrationName} Integration`,
        widgetConnections?.map(c => c.name),
      )
    : `${humanizedIntegrationName} Integration`;

  const onAddNewConnection = async () => {
    const newConnection: ConnectionModel = await create(activeFbPageId, {
      active: false,
      name: incrementedName,
      kind: ConnectionKind.WIDGET,
      connectionKind: IntegrationTypesToWebhookKind[type],
    });
    fetchConnections();

    if (newConnection) {
      onEditConnection(newConnection.id);
      onConnectionChange(newConnection);
    }
  };

  const onConnectionUpdate = async (
    connection: ConnectionModel,
    params: Partial<ConnectionModel>,
    onSuccess?: (connection?: ConnectionModel) => void,
  ) => {
    if (connection?.id) {
      const newConnection = await update(
        activeFbPageId,
        {
          id: connection.id,
          ...params,
        },
        type,
      );
      onSuccess?.(newConnection);
    }
  };

  const createOrUpdateConnection = (
    connection?: ConnectionModel,
    params?: Partial<ConnectionModel>,
    onSuccess?: (connection?: ConnectionModel) => void,
  ) => {
    if (connection && params) {
      onConnectionUpdate(connection, params, onSuccess);
    } else {
      onAddNewConnection();
    }
  };

  return {
    fetchConnections,
    getIntegrationWidgetConfig,
    onAddNewConnection,
    onConnectionUpdate,
    createOrUpdateConnection,
    onConnectionChange,
    onEditConnection,
  };
};
